import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, ArrowRight, Check} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {
  continueButtonLabel,
  isProductStep,
  STEP,
  stepProduct,
} from '@client/pages/public/enroll/enrollModel';
import type {ProductConfig} from '@client/pages/public/enroll/types';
import {DeclineMedicalDialog} from '@client/pages/public/enroll/components/DeclineMedicalDialog';
import {SkipProductDialog} from '@client/pages/public/enroll/components/SkipProductDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';

export function StepNav() {
  const {
    group,
    steps,
    currentStep,
    currentIndex,
    canContinue,
    selections,
    declineProduct,
    clearSelection,
  } = useEnrollSteps();
  const navigate = useNavigate();
  const [declineMedicalOpen, setDeclineMedicalOpen] = useState(false);
  const [productToSkip, setProductToSkip] = useState<ProductConfig | null>(null);
  const base = `/${group.slug}`;

  if (!currentStep) return null;

  const prev = currentIndex > 0 ? steps[currentIndex - 1] : undefined;
  const next = currentIndex < steps.length - 1 ? steps[currentIndex + 1] : undefined;

  const currentProduct = isProductStep(currentStep) ? stepProduct(group, currentStep) : undefined;
  const isDeclinable = currentProduct?.declinable ?? false;
  const isDeclined = currentProduct ? selections[currentProduct.category]?.declined : false;
  const usesSkipWording = currentProduct?.declineStyle === 'skipped';
  // Declining medical (MEC/ACA waiver) requires an explicit acknowledgment; other products skip instantly.
  const requiresDeclineConfirmation = currentProduct?.category === STEP.Medical;

  const continueLabel = continueButtonLabel(currentStep);
  // On the gated product steps, explain the disabled Continue via a tooltip instead of a static banner.
  const showContinueHint = isProductStep(currentStep) && !canContinue;

  const continueButton = next && (
    <button
      type='button'
      disabled={!canContinue}
      onClick={() => navigate(`${base}/${next}`)}
      className='inline-flex h-11 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--enroll-accent-hover)] disabled:pointer-events-none disabled:opacity-40'
    >
      {continueLabel}
      <ArrowRight className='h-4 w-4' />
    </button>
  );

  function handleDeclineClick() {
    if (!currentProduct) return;
    if (isDeclined) {
      clearSelection(currentProduct.category);
    } else if (requiresDeclineConfirmation) {
      setDeclineMedicalOpen(true);
    } else if (usesSkipWording) {
      setProductToSkip(currentProduct);
    } else {
      declineProduct(currentProduct.category);
    }
  }

  return (
    <>
      <div className={cn('flex flex-col gap-4 border-t border-neutral-200 pt-6 sm:flex-row sm:items-center sm:justify-between', POSTHOG_UNMASK_CLASS)}>
        <div>
          {isDeclinable && currentProduct && (
            <button
              type='button'
              onClick={handleDeclineClick}
              title={isDeclined ? 'Click to choose a plan instead' : undefined}
              className={cn(
                'inline-flex h-11 items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition-colors',
                isDeclined
                  ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
                  : 'border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700',
              )}
            >
              {isDeclined ? (
                <>
                  <Check className='h-4 w-4' />
                  {usesSkipWording
                    ? `${currentProduct.label} Skipped`
                    : `${currentProduct.label} Coverage Declined`}
                </>
              ) : usesSkipWording ? (
                `Skip ${currentProduct.label}`
              ) : (
                `I decline ${currentProduct.label} coverage`
              )}
            </button>
          )}
        </div>

        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={() => navigate(prev ? `${base}/${prev}` : base)}
            className='inline-flex h-11 items-center gap-2 rounded-lg border border-neutral-300 px-4 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100'
          >
            <ArrowLeft className='h-4 w-4' />
            Back
          </button>
          {showContinueHint ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex cursor-not-allowed'>{continueButton}</span>
                </TooltipTrigger>
                <TooltipContent className='border border-amber-300 bg-amber-50 font-medium text-amber-900'>
                  Make a selection or decline coverage to continue.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            continueButton
          )}
        </div>
      </div>

      {currentProduct && requiresDeclineConfirmation && (
        <DeclineMedicalDialog
          open={declineMedicalOpen}
          onOpenChange={setDeclineMedicalOpen}
          onConfirm={() => {
            declineProduct(currentProduct.category);
            setDeclineMedicalOpen(false);
          }}
        />
      )}

      {productToSkip && (
        <SkipProductDialog
          open
          onOpenChange={(open) => {
            if (!open) setProductToSkip(null);
          }}
          productLabel={productToSkip.label}
          onConfirm={() => {
            declineProduct(productToSkip.category);
            setProductToSkip(null);
          }}
        />
      )}
    </>
  );
}
